import { FC, MouseEvent, ReactNode, useEffect, useMemo, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  ChevronLeft,
  ChevronRight,
  CommitOutlined,
  FilterAltOutlined,
  Remove,
  RotateLeft,
  RotateRight,
  ShowChart,
} from "@mui/icons-material";
import { Box, Button, IconButton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";

import { useAnalysisTools } from "../../../../../../context/analysis-tools-context";
import {
  FrequencyProfileIcon,
  HarmonicCursorsIcon,
  MarkerIcon,
  SubHarmonicCursorsIcon,
} from "../../../multi-analysis/components/toolbar/toolbar-icons";
import { useToolbarScroll } from "../../../multi-analysis/hooks/use-toolbar-scroll";
import {
  BODE_FREQUENCY_AXIS_SCALE,
  BODE_FREQUENCY_SCALE,
  CASCADE_SORT_MODE,
  MULTI_ANALYSIS_MEASURE_TYPE,
  ROTATION_DIRECTION,
  SIGNAL_DISPLAY_UNIT,
  SIGNAL_TIME_SCALE,
  SPECTRUM_FREQUENCY_SCALE,
  type TBodeFrequencyAxisScale,
  type TCascadeSortMode,
  type TSignalDisplayUnit,
} from "../../../multi-analysis/types";
import { rotationDirectionIconSx } from "../../../scheme/components/analytics/components/multiline-chart-orbit/styled";
import type { SpeedScreenForm } from "../../hooks/use-speed-screen-forms";
import { BandConstructionIcon, OrbitOverlayIcon, RevolutionConstructionIcon } from "./toolbar-icons";

interface IToolbarToggleButtonProps {
  titleId: string;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}

const ToolbarToggleButton: FC<IToolbarToggleButtonProps> = ({
  titleId,
  selected,
  onClick,
  disabled = false,
  children,
}) => (
  <Tooltip title={<FormattedMessage id={titleId} />} arrow disableInteractive>
    <span>
      <ToggleButton
        value={titleId}
        selected={selected}
        onClick={onClick}
        disabled={disabled}
        size="small"
        sx={{
          flexShrink: 0,
          width: 34,
          height: 34,
          p: 0.5,
          border: "1px solid #d8ddea",
          "&.Mui-selected": {
            bgcolor: "rgba(63, 109, 181, 0.12)",
            borderColor: "#3f6db5",
            color: "#3f6db5",
          },
        }}
      >
        {children}
      </ToggleButton>
    </span>
  </Tooltip>
);

const ScrollArrowButton: FC<{ direction: "left" | "right"; disabled: boolean; onClick: () => void }> = ({
  direction,
  disabled,
  onClick,
}) => (
  <IconButton
    size="small"
    disabled={disabled}
    onClick={onClick}
    aria-label={direction === "left" ? "scroll left" : "scroll right"}
    sx={{
      flexShrink: 0,
      width: 28,
      height: 34,
      color: disabled ? "#b8bfd0" : "#4f5d75",
    }}
  >
    {direction === "left" ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
  </IconButton>
);

const SignalTimeScaleButton: FC<{
  revolutions: boolean;
  onClick: () => void;
}> = ({ revolutions, onClick }) => {
  const { formatMessage } = useIntl();
  const title = formatMessage({ id: "pages.multiAnalysis.toolbar.signalTimeScale" });

  return (
    <Tooltip title={title} arrow disableInteractive>
      <span>
        <Button
          size="small"
          variant="outlined"
          aria-label={title}
          onClick={onClick}
          sx={{
            flexShrink: 0,
            minWidth: 48,
            height: 34,
            p: 0.5,
            fontSize: 11,
            fontWeight: 700,
            borderColor: "#d8ddea",
            color: "#3f6db5",
          }}
        >
          {revolutions ? "RPM" : "T"}
        </Button>
      </span>
    </Tooltip>
  );
};

const BodeFrequencyScaleButton: FC<{ rpm: boolean; onClick: () => void }> = ({ rpm, onClick }) => {
  const { formatMessage } = useIntl();
  const title = formatMessage({ id: "pages.speed.bode.toolbar.frequencyScale" });

  return (
    <Tooltip title={title} arrow disableInteractive>
      <span>
        <Button
          size="small"
          variant="outlined"
          aria-label={title}
          onClick={onClick}
          sx={{
            flexShrink: 0,
            minWidth: 48,
            height: 34,
            p: 0.5,
            fontSize: 11,
            fontWeight: 700,
            borderColor: "#d8ddea",
            color: "#3f6db5",
          }}
        >
          {rpm ? formatMessage({ id: "pages.scheme.analytics.rotationFrequencyRpmUnit" }) : "Hz"}
        </Button>
      </span>
    </Tooltip>
  );
};

const SIGNAL_UNIT_OPTIONS: readonly { value: TSignalDisplayUnit; label: string; titleId: string }[] = [
  {
    value: SIGNAL_DISPLAY_UNIT.ACCELERATION,
    label: "A",
    titleId: "pages.multiAnalysis.toolbar.signalUnitsAcceleration",
  },
  { value: SIGNAL_DISPLAY_UNIT.VELOCITY, label: "V", titleId: "pages.multiAnalysis.toolbar.signalUnitsVelocity" },
  {
    value: SIGNAL_DISPLAY_UNIT.DISPLACEMENT,
    label: "S",
    titleId: "pages.multiAnalysis.toolbar.signalUnitsDisplacement",
  },
];

const segmentedToggleGroupSx = {
  flexShrink: 0,
  "& .MuiToggleButton-root": {
    width: 34,
    height: 34,
    p: 0,
    fontSize: 11,
    fontWeight: 700,
    border: "1px solid #d8ddea",
    "&.Mui-selected": {
      bgcolor: "rgba(63, 109, 181, 0.12)",
      borderColor: "#3f6db5",
      color: "#3f6db5",
    },
  },
} as const;

const SignalUnitsToggle: FC<{
  value: TSignalDisplayUnit;
  disabled?: boolean;
  onChange: (value: TSignalDisplayUnit) => void;
}> = ({ value, disabled = false, onChange }) => {
  const { formatMessage } = useIntl();
  const handleChange = (_: MouseEvent<HTMLElement>, nextValue: string | null) => {
    if (nextValue !== null) onChange(Number(nextValue) as TSignalDisplayUnit);
  };

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={String(value)}
      disabled={disabled}
      onChange={handleChange}
      aria-label={formatMessage({ id: "pages.multiAnalysis.toolbar.signalUnits" })}
      sx={segmentedToggleGroupSx}
    >
      {SIGNAL_UNIT_OPTIONS.map(option => (
        <ToggleButton
          key={option.value}
          value={String(option.value)}
          aria-label={formatMessage({ id: option.titleId })}
        >
          <Tooltip title={formatMessage({ id: option.titleId })} arrow disableInteractive>
            <Box component="span">{option.label}</Box>
          </Tooltip>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
};

const SpectrumFrequencyScaleButton: FC<{ orders: boolean; onClick: () => void }> = ({ orders, onClick }) => {
  const { formatMessage } = useIntl();
  const title = formatMessage({ id: "pages.multiAnalysis.toolbar.spectrumFrequencyScale" });

  return (
    <Tooltip title={title} arrow disableInteractive>
      <span>
        <Button
          size="small"
          variant="outlined"
          aria-label={title}
          onClick={onClick}
          sx={{
            flexShrink: 0,
            minWidth: 48,
            height: 34,
            p: 0.5,
            fontSize: 11,
            fontWeight: 700,
            borderColor: "#d8ddea",
            color: "#3f6db5",
          }}
        >
          {orders ? "Or" : "Hz"}
        </Button>
      </span>
    </Tooltip>
  );
};

const CascadeSortModeToggle: FC<{
  value: TCascadeSortMode;
  onChange: (value: TCascadeSortMode) => void;
}> = ({ value, onChange }) => {
  const { formatMessage } = useIntl();
  const title = formatMessage({ id: "pages.speed.cascade.toolbar.sortMode" });
  const handleChange = (_: MouseEvent<HTMLElement>, nextValue: TCascadeSortMode | null) => {
    if (nextValue !== null) onChange(nextValue);
  };

  return (
    <Tooltip title={title} arrow disableInteractive>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={value}
        onChange={handleChange}
        aria-label={title}
        sx={{
          flexShrink: 0,
          "& .MuiToggleButton-root": {
            minWidth: 34,
            height: 34,
            px: 0.75,
            py: 0,
            fontSize: 10,
            fontWeight: 700,
            border: "1px solid #d8ddea",
            "&.Mui-selected": {
              bgcolor: "rgba(63, 109, 181, 0.12)",
              borderColor: "#3f6db5",
              color: "#3f6db5",
            },
          },
        }}
      >
        <ToggleButton value={CASCADE_SORT_MODE.RPM}>
          {formatMessage({ id: "pages.speed.cascade.cascade" })}
        </ToggleButton>
        <ToggleButton value={CASCADE_SORT_MODE.TIME}>
          {formatMessage({ id: "pages.speed.cascade.waterfall" })}
        </ToggleButton>
      </ToggleButtonGroup>
    </Tooltip>
  );
};

const CascadeDisplayToggle: FC<{
  display3D: boolean;
  onChange: (display3D: boolean) => void;
}> = ({ display3D, onChange }) => {
  const { formatMessage } = useIntl();
  const title = formatMessage({ id: "pages.speed.cascade.toolbar.display3d" });
  const handleChange = (_: MouseEvent<HTMLElement>, nextValue: "2d" | "3d" | null) => {
    if (nextValue !== null) onChange(nextValue === "3d");
  };

  return (
    <Tooltip title={title} arrow disableInteractive>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={display3D ? "3d" : "2d"}
        onChange={handleChange}
        aria-label={title}
        sx={{
          flexShrink: 0,
          "& .MuiToggleButton-root": {
            minWidth: 34,
            height: 34,
            px: 0.75,
            py: 0,
            fontSize: 10,
            fontWeight: 700,
            border: "1px solid #d8ddea",
            "&.Mui-selected": {
              bgcolor: "rgba(63, 109, 181, 0.12)",
              borderColor: "#3f6db5",
              color: "#3f6db5",
            },
          },
        }}
      >
        <ToggleButton value="2d">{formatMessage({ id: "pages.scheme.analytics.spectrumLayout2d" })}</ToggleButton>
        <ToggleButton value="3d">{formatMessage({ id: "pages.scheme.analytics.spectrumLayout3d" })}</ToggleButton>
      </ToggleButtonGroup>
    </Tooltip>
  );
};

const BodeFrequencyAxisScaleToggle: FC<{
  value: TBodeFrequencyAxisScale;
  onChange: (value: TBodeFrequencyAxisScale) => void;
}> = ({ value, onChange }) => {
  const { formatMessage } = useIntl();
  const title = formatMessage({ id: "pages.speed.bode.toolbar.frequencyAxisScale" });
  const handleChange = (_: MouseEvent<HTMLElement>, nextValue: TBodeFrequencyAxisScale | null) => {
    if (nextValue !== null) onChange(nextValue);
  };

  return (
    <Tooltip title={title} arrow disableInteractive>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={value}
        onChange={handleChange}
        aria-label={title}
        sx={{
          flexShrink: 0,
          "& .MuiToggleButton-root": {
            minWidth: 34,
            height: 34,
            px: 0.75,
            py: 0,
            fontSize: 10,
            fontWeight: 700,
            border: "1px solid #d8ddea",
            "&.Mui-selected": {
              bgcolor: "rgba(63, 109, 181, 0.12)",
              borderColor: "#3f6db5",
              color: "#3f6db5",
            },
          },
        }}
      >
        <ToggleButton value={BODE_FREQUENCY_AXIS_SCALE.MIN_MAX}>MIN-MAX</ToggleButton>
        <ToggleButton value={BODE_FREQUENCY_AXIS_SCALE.ZERO}>0</ToggleButton>
      </ToggleButtonGroup>
    </Tooltip>
  );
};

interface ISpeedToolbarProps {
  isRotationDirectionLocked?: boolean;
  activeFormId?: SpeedScreenForm | null;
}

export const SpeedToolbar: FC<ISpeedToolbarProps> = ({ isRotationDirectionLocked = false, activeFormId = null }) => {
  const { formatMessage } = useIntl();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { activeMeasureType, toolbarState, setToolbarState } = useAnalysisTools();
  const toolsKey = activeFormId ?? activeMeasureType ?? "none";
  const { canScrollLeft, canScrollRight, scrollLeft, scrollRight } = useToolbarScroll(scrollRef, toolsKey);

  useEffect(() => {
    if (!toolbarState.orbitRevolutionConstruction) {
      return;
    }

    if (!toolbarState.orbitBandConstruction && toolbarState.orbitBandSelection == null) {
      return;
    }

    setToolbarState({ orbitBandConstruction: false, orbitBandSelection: null });
  }, [
    setToolbarState,
    toolbarState.orbitBandConstruction,
    toolbarState.orbitBandSelection,
    toolbarState.orbitRevolutionConstruction,
  ]);

  const tools = useMemo(() => {
    if (activeFormId === "cascade") {
      return (
        <>
          <CascadeSortModeToggle
            value={toolbarState.cascadeSortMode}
            onChange={cascadeSortMode => setToolbarState({ cascadeSortMode })}
          />
          <ToolbarToggleButton
            titleId="pages.speed.cascade.toolbar.showTrend"
            selected={toolbarState.cascadeShowTrend}
            onClick={() => {
              const cascadeShowTrend = !toolbarState.cascadeShowTrend;
              setToolbarState({
                cascadeShowTrend,
                ...(cascadeShowTrend
                  ? {}
                  : { frequencyProfile: false, frequencyProfileSelection: null, cascadeProfileRpm: null }),
              });
            }}
          >
            <ShowChart fontSize="small" />
          </ToolbarToggleButton>
          <SpectrumFrequencyScaleButton
            orders={toolbarState.spectrumFrequencyScale === SPECTRUM_FREQUENCY_SCALE.ORDERS}
            onClick={() =>
              setToolbarState({
                spectrumFrequencyScale:
                  toolbarState.spectrumFrequencyScale === SPECTRUM_FREQUENCY_SCALE.ORDERS
                    ? SPECTRUM_FREQUENCY_SCALE.HERTZ
                    : SPECTRUM_FREQUENCY_SCALE.ORDERS,
              })
            }
          />
          <SignalUnitsToggle
            value={toolbarState.signalDisplayUnits}
            disabled={!toolbarState.cascadeCanConvertUnits}
            onChange={signalDisplayUnits => setToolbarState({ signalDisplayUnits })}
          />
          <ToolbarToggleButton
            titleId="pages.multiAnalysis.toolbar.marker"
            selected={toolbarState.markerMode}
            onClick={() => {
              const markerMode = !toolbarState.markerMode;
              setToolbarState({
                markerMode,
                ...(markerMode ? {} : { cascadeMarkerFrequencyHz: null, cascadeMarkerRpm: null }),
              });
            }}
          >
            <MarkerIcon />
          </ToolbarToggleButton>
          <ToolbarToggleButton
            titleId="pages.multiAnalysis.toolbar.harmonic"
            selected={toolbarState.harmonicCursors}
            onClick={() => setToolbarState({ harmonicCursors: !toolbarState.harmonicCursors })}
          >
            <HarmonicCursorsIcon />
          </ToolbarToggleButton>
          <ToolbarToggleButton
            titleId="pages.multiAnalysis.toolbar.subharmonic"
            selected={toolbarState.subHarmonicCursors}
            onClick={() => setToolbarState({ subHarmonicCursors: !toolbarState.subHarmonicCursors })}
          >
            <SubHarmonicCursorsIcon />
          </ToolbarToggleButton>
          <ToolbarToggleButton
            titleId="pages.multiAnalysis.toolbar.frequencyProfile"
            selected={toolbarState.frequencyProfile}
            disabled={!toolbarState.cascadeShowTrend}
            onClick={() => {
              if (!toolbarState.cascadeShowTrend) return;
              const frequencyProfile = !toolbarState.frequencyProfile;
              setToolbarState({
                frequencyProfile,
                ...(frequencyProfile ? {} : { frequencyProfileSelection: null, cascadeProfileRpm: null }),
              });
            }}
          >
            <FrequencyProfileIcon />
          </ToolbarToggleButton>
          <CascadeDisplayToggle
            display3D={toolbarState.cascadeDisplay3D}
            onChange={cascadeDisplay3D => setToolbarState({ cascadeDisplay3D })}
          />
        </>
      );
    }

    if (activeFormId === "bode") {
      return (
        <>
          <BodeFrequencyScaleButton
            rpm={toolbarState.bodeFrequencyScale === BODE_FREQUENCY_SCALE.RPM}
            onClick={() =>
              setToolbarState({
                bodeFrequencyScale:
                  toolbarState.bodeFrequencyScale === BODE_FREQUENCY_SCALE.RPM
                    ? BODE_FREQUENCY_SCALE.HERTZ
                    : BODE_FREQUENCY_SCALE.RPM,
              })
            }
          />
          <BodeFrequencyAxisScaleToggle
            value={toolbarState.bodeFrequencyAxisScale}
            onChange={bodeFrequencyAxisScale => setToolbarState({ bodeFrequencyAxisScale })}
          />
          <ToolbarToggleButton
            titleId="pages.multiAnalysis.toolbar.marker"
            selected={toolbarState.markerMode}
            onClick={() => {
              const nextMarkerMode = !toolbarState.markerMode;

              setToolbarState({
                markerMode: nextMarkerMode,
                ...(nextMarkerMode
                  ? {}
                  : {
                      syncSpectrumCursor: toolbarState.syncSpectrumCursor
                        ? { ...toolbarState.syncSpectrumCursor, markerPhysicalX: null }
                        : null,
                      syncSignalCursor: toolbarState.syncSignalCursor
                        ? { ...toolbarState.syncSignalCursor, markerPhysicalX: null }
                        : null,
                    }),
              });
            }}
          >
            <MarkerIcon />
          </ToolbarToggleButton>
        </>
      );
    }

    if (
      activeMeasureType !== MULTI_ANALYSIS_MEASURE_TYPE.POLAR_TREND &&
      activeMeasureType !== MULTI_ANALYSIS_MEASURE_TYPE.ORBIT
    ) {
      return null;
    }

    const rotationTools = (
      <Box
        aria-label={formatMessage({ id: "pages.multiAnalysis.toolbar.rotation" })}
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
        }}
      >
        <ToolbarToggleButton
          titleId="enums.rotationDirection.counterClockwise"
          selected={toolbarState.rotationDirection === ROTATION_DIRECTION.COUNTER_CLOCKWISE}
          onClick={() => setToolbarState({ rotationDirection: ROTATION_DIRECTION.COUNTER_CLOCKWISE })}
          disabled={isRotationDirectionLocked}
        >
          <RotateLeft sx={rotationDirectionIconSx} />
        </ToolbarToggleButton>
        <ToolbarToggleButton
          titleId="enums.rotationDirection.undefined"
          selected={toolbarState.rotationDirection === ROTATION_DIRECTION.UNDEFINED}
          onClick={() => setToolbarState({ rotationDirection: ROTATION_DIRECTION.UNDEFINED })}
          disabled={isRotationDirectionLocked}
        >
          <Remove sx={rotationDirectionIconSx} />
        </ToolbarToggleButton>
        <ToolbarToggleButton
          titleId="enums.rotationDirection.clockwise"
          selected={toolbarState.rotationDirection === ROTATION_DIRECTION.CLOCKWISE}
          onClick={() => setToolbarState({ rotationDirection: ROTATION_DIRECTION.CLOCKWISE })}
          disabled={isRotationDirectionLocked}
        >
          <RotateRight sx={rotationDirectionIconSx} />
        </ToolbarToggleButton>
      </Box>
    );

    if (activeMeasureType === MULTI_ANALYSIS_MEASURE_TYPE.POLAR_TREND) {
      return (
        <>
          {rotationTools}
          <ToolbarToggleButton
            titleId="pages.speed.nyquist.toolbar.amplitudePhase"
            selected={toolbarState.showNyquistAmplitudePhase}
            onClick={() => setToolbarState({ showNyquistAmplitudePhase: !toolbarState.showNyquistAmplitudePhase })}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 700, lineHeight: 1 }}>A/φ</Typography>
          </ToolbarToggleButton>
          <ToolbarToggleButton
            titleId="pages.speed.nyquist.toolbar.rotationFrequency"
            selected={toolbarState.showNyquistRotationFrequency}
            onClick={() =>
              setToolbarState({ showNyquistRotationFrequency: !toolbarState.showNyquistRotationFrequency })
            }
          >
            <Typography sx={{ fontSize: 10, fontWeight: 700, lineHeight: 1 }}>RPM</Typography>
          </ToolbarToggleButton>
        </>
      );
    }

    return (
      <>
        {rotationTools}
        <ToolbarToggleButton
          titleId="pages.multiAnalysis.toolbar.orbitPoints"
          selected={toolbarState.showOrbitPoints}
          disabled={toolbarState.orbitRevolutionConstruction}
          onClick={() => {
            if (toolbarState.orbitRevolutionConstruction) {
              return;
            }

            setToolbarState({ showOrbitPoints: !toolbarState.showOrbitPoints });
          }}
        >
          <CommitOutlined fontSize="small" />
        </ToolbarToggleButton>
        <ToolbarToggleButton
          titleId="pages.multiAnalysis.toolbar.orbitOverlay"
          selected={toolbarState.orbitOverlay}
          onClick={() => setToolbarState({ orbitOverlay: !toolbarState.orbitOverlay })}
        >
          <OrbitOverlayIcon />
        </ToolbarToggleButton>
        <ToolbarToggleButton
          titleId="pages.multiAnalysis.toolbar.orbitFilter"
          selected={
            toolbarState.orbitFilterOpen ||
            toolbarState.orbitAppliedBins.length > 0 ||
            toolbarState.orbitAppliedBandpassHz !== null
          }
          onClick={() => setToolbarState({ orbitFilterOpen: !toolbarState.orbitFilterOpen })}
        >
          <FilterAltOutlined fontSize="small" />
        </ToolbarToggleButton>
        <ToolbarToggleButton
          titleId="pages.multiAnalysis.toolbar.bandConstruction"
          selected={toolbarState.orbitBandConstruction && !toolbarState.orbitRevolutionConstruction}
          disabled={toolbarState.orbitRevolutionConstruction}
          onClick={() => {
            if (toolbarState.orbitRevolutionConstruction) {
              return;
            }

            const enabled = !toolbarState.orbitBandConstruction;
            setToolbarState({
              orbitBandConstruction: enabled,
              ...(enabled ? {} : { orbitBandSelection: null }),
            });
          }}
        >
          <BandConstructionIcon />
        </ToolbarToggleButton>
        <ToolbarToggleButton
          titleId="pages.multiAnalysis.toolbar.revolutionConstruction"
          selected={toolbarState.orbitRevolutionConstruction && !toolbarState.orbitBandConstruction}
          disabled={toolbarState.orbitBandConstruction}
          onClick={() => {
            if (toolbarState.orbitBandConstruction) {
              return;
            }

            const enabled = !toolbarState.orbitRevolutionConstruction;
            setToolbarState({
              orbitRevolutionConstruction: enabled,
              ...(enabled ? { showOrbitPoints: true } : { orbitRevolutionSelection: null }),
            });
          }}
        >
          <RevolutionConstructionIcon />
        </ToolbarToggleButton>
        <SignalTimeScaleButton
          revolutions={toolbarState.signalTimeScale === SIGNAL_TIME_SCALE.REVOLUTIONS}
          onClick={() =>
            setToolbarState({
              signalTimeScale:
                toolbarState.signalTimeScale === SIGNAL_TIME_SCALE.REVOLUTIONS
                  ? SIGNAL_TIME_SCALE.TIME
                  : SIGNAL_TIME_SCALE.REVOLUTIONS,
            })
          }
        />
      </>
    );
  }, [activeFormId, activeMeasureType, formatMessage, isRotationDirectionLocked, setToolbarState, toolbarState]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, px: 0.25, py: 0.5 }}>
      <ScrollArrowButton direction="left" disabled={!canScrollLeft} onClick={scrollLeft} />
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          overflowX: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          px: 0.5,
        }}
      >
        {tools ?? (
          <Typography sx={{ fontSize: 12, color: "#8b93a7", px: 0.5, whiteSpace: "nowrap" }}>
            <FormattedMessage id="pages.multiAnalysis.toolbar.noTools" />
          </Typography>
        )}
      </Box>
      <ScrollArrowButton direction="right" disabled={!canScrollRight} onClick={scrollRight} />
    </Box>
  );
};
